/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { createContext, useContext, useEffect } from 'react';
import type { SnapIntegrations, SnapIntegrationsProviderProps } from './types';
import { warnMissingSnapIntegrations } from './validateIntegrations';

type SnapIntegrationsContextType = Readonly<Partial<SnapIntegrations>>;

// Snap owns this contract; each host supplies its Flight implementation.
const SnapIntegrationsContext = createContext<
  SnapIntegrationsContextType | undefined
>(undefined);

const SnapIntegrationsProvider = ({
  children,
  integrations,
}: SnapIntegrationsProviderProps) => {
  useEffect(() => {
    // Development-only early warning; SearchFormView still hides the one-way
    // row when the adapter is omitted.
    warnMissingSnapIntegrations(integrations);
  }, [integrations]);

  // The host owns the object identity; this provider only exposes its adapters.
  return (
    <SnapIntegrationsContext.Provider value={integrations}>
      {children}
    </SnapIntegrationsContext.Provider>
  );
};

const useIntegrationContext = (): SnapIntegrationsContextType => {
  const integrations = useContext(SnapIntegrationsContext);

  // A consumer of a required integration outside the provider is a host setup error.
  if (!integrations) {
    throw new Error(
      'Snap integrations must be used inside SnapIntegrationsProvider',
    );
  }

  return integrations;
};

export const useSnapIntegration = <Key extends keyof SnapIntegrations>(
  key: Key,
): SnapIntegrations[Key] => {
  const integration = useIntegrationContext()[key];

  if (!integration) {
    throw new Error(
      `Snap integration "${key}" is required for this feature. Provide it through SnapIntegrationsProvider.`,
    );
  }

  return integration as SnapIntegrations[Key];
};

export const useOptionalSnapIntegration = <Key extends keyof SnapIntegrations>(
  key: Key,
): SnapIntegrations[Key] | undefined => {
  // Home-web is not wired yet: missing provider must not crash SNAP search.
  const integrations = useContext(SnapIntegrationsContext);

  if (!integrations) {
    return undefined;
  }

  return integrations[key] as SnapIntegrations[Key] | undefined;
};

export default SnapIntegrationsProvider;
